/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1747872000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Thu May 22 2025 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "kognitiv",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Getting started",
          "depth": 0,
          "id": "getting-started"
        }, {
          "value": "Connect to Kognitiv",
          "depth": 1,
          "id": "connect-to-kognitiv"
        }, {
          "value": "Supported authentication methods",
          "depth": 1,
          "id": "supported-authentication-methods"
        }, {
          "value": "PGP encryption",
          "depth": 1,
          "id": "pgp-encryption"
        }, {
          "value": "Syncing data",
          "depth": 0,
          "id": "syncing-data"
        }, {
          "value": "File import specifications",
          "depth": 1,
          "id": "file-import-specifications"
        }, {
          "value": "Field mapping",
          "depth": 1,
          "id": "field-mapping"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      p: "p",
      h3: "h3",
      a: "a",
      strong: "strong",
      ul: "ul",
      li: "li",
      code: "code",
      pre: "pre"
    }, _provideComponents(), props.components), {NoCommonErrors, LiveDebugger, SyncAlerts} = _components;
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!NoCommonErrors) _missingMdxReference("NoCommonErrors", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Any data set"
            }), _jsx(_components.td, {
              children: "Sync data from a source to a file-import specification in Kognitiv"
            }), _jsx(_components.td, {
              children: "Mirror"
            })]
          })
        })]
      }), "\n", _jsx(_components.p, {
        children: "Mirror mode means one file with all the rows in the query results will be created, every time the sync runs."
      }), "\n", _jsx(_components.h2, {
        id: "getting-started",
        children: "Getting started"
      }), "\n", _jsx(_components.h3, {
        id: "connect-to-kognitiv",
        children: "Connect to Kognitiv"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Kognitiv"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "SFTP"
        }), " by entering the following required fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Hostname"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Port"
          })
        }), "\n", _jsxs(_components.li, {
          children: ["Credentials (see the ", _jsx(_components.a, {
            href: "#supported-authentication-methods",
            children: "supported authentication methods"
          }), " below)"]
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["The user needs to have access to the target directory and file with ", _jsx(_components.strong, {
            children: "write privileges"
          }), ".\nA ", _jsx(_components.code, {
            children: "permission denied"
          }), " error message during a sync indicates the user may not have write permissions for both the directory and the file."]
        })
      }), "\n", _jsx(WhitelistDetails, {
        target: "SFTP server"
      }), "\n", _jsx(_components.h3, {
        id: "supported-authentication-methods",
        children: "Supported authentication methods"
      }), "\n", _jsx(_components.p, {
        children: "We support three authentication methods:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Username + password"
        }), "\n", _jsx(_components.li, {
          children: "Username + SSH private key"
        }), "\n", _jsx(_components.li, {
          children: "Username + SSH private key + passphrase"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "We support two formats for the SSH private key:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "OpenSSH"
        }), "\n", _jsx(_components.li, {
          children: "RSA"
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Provide the full private key for the user, including the header and footer."
      }), "\n", _jsx(_components.p, {
        children: "For example:"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "-----BEGIN OPENSSH PRIVATE KEY-----\n<private key body>\n<private key body>\n<private key body>\n-----END OPENSSH PRIVATE KEY-----\n"
        })
      }), "\n", _jsx(_components.h3, {
        id: "pgp-encryption",
        children: "PGP encryption"
      }), "\n", _jsxs(_components.p, {
        children: ["The Kognitiv destination supports PGP encryption. Given a ", _jsx(_components.strong, {
          children: "PGP public key"
        }), ", all files transferred to the destination will be encrypted."]
      }), "\n", _jsxs(_components.p, {
        children: ["To use PGP encryption, provide your public key in the ", _jsx(_components.strong, {
          children: "PGP public key"
        }), " text area including the header and footer in the following format:"]
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-bash",
          children: "-----BEGIN PGP PUBLIC KEY BLOCK-----\n\n<PGP public key body>\n<PGP public key body>\n<PGP public key body>\n-----END PGP PUBLIC KEY BLOCK-----\n"
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(_components.strong, {
            children: "PGP private key"
          }), " is required to decrypt the files synced to your destination.\nYou don't need to insert this value in Hightouch."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "syncing-data",
        children: "Syncing data"
      }), "\n", _jsx(_components.h3, {
        id: "file-import-specifications",
        children: "File import specifications"
      }), "\n", _jsx(_components.p, {
        children: "Choose which file import specification you'd like to import to Kognitiv. Hightouch offers templates for:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Member descriptor file"
        }), "\n", _jsx(_components.li, {
          children: "Loyalty member custom fields"
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Access the complete list of Kognitiv's file import options ", _jsx(_components.a, {
          href: "https://info.kognitivloyalty.com/File_Import_Specifications.html",
          children: "here"
        }), ".\nIf you'd like to sync to a file import specification that isn't listed, select ", _jsx(_components.strong, {
          children: "Custom"
        }), " and enter in the column fields as mappings."]
      }), "\n", _jsx(_components.h3, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "If you selected one of the file import specifications listed above, column mapping options will be available to select from. You can add additional mappings by creating a new field."
      }), "\n", _jsxs(_components.p, {
        children: ["If you selected ", _jsx(_components.strong, {
          children: "Custom"
        }), " file import specification, enter the field that you'd like to map data to."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(NoCommonErrors, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
